// @charitystack/org-engagement-contracts — paragon module, shared value shapes.
//
// Strict Zod schemas + inferred types transcribed from the paragon-backend
// executable legacy contract (`contracts-legacy/paragon-backend/schemas.ts`,
// pinned `origin/dev` = `7cd48d532bf395c1313aa1267b113907ac8e0d57`, refreshed
// 2026-09-11; `origin/main` = `5db8434daa95d3446806ae2fa27f1cd99db1e359` resolves
// to the byte-identical tree, so these shapes describe prod as deployed).
// Schemas and pure derivations only: no router, no runtime client, no I/O.
//
// Deliberately absent (INF-105 precedent, milestone ledger): the legacy
// contract's parity machinery — Lambda proxy envelope, gateway variant
// registries (AUTHED_/PUBLIC_GATEWAY_VARIANTS, per-route *_VARIANTS), the
// header-map constants and `parseAgainst`/`PARSERS` replay tooling. Those pin
// the recorder oracle, not a shared FE↔BE shape.

import { z } from 'zod';

/**
 * Any JSON value a caller supplied that the legacy handler echoes to the wire or
 * writes to DynamoDB WITHOUT coercing or validating it (`virtuous_webhook.py`
 * coerces nothing; `zapier_webhook_url.py` coerces only `zapierConnectionEnabled`
 * through `bool(...)`). Deliberately NOT `z.any()` — that would also admit
 * `undefined`, which cannot survive `json.dumps`. Containers stay opaque because
 * the code never inspects their interiors. Bug-for-bug: the missing validation is
 * an `x-deviations` finding in the legacy repo, not fixed here.
 */
export const uncoercedCallerValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.unknown()),
  z.record(z.unknown()),
]);
export type UncoercedCallerValue = z.infer<typeof uncoercedCallerValueSchema>;

/**
 * `jwt.encode(payload, key, algorithm='RS256')` in `get_jwt.py:create_jwt` — PyJWT
 * returns a compact JWS: three base64url segments, and an RS256 signature is never
 * empty. Pinned as a shape, NOT parsed: the recorder must never be handed a real
 * token to compare against.
 */
export const COMPACT_JWS = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

/**
 * `PARAGON_PROJECT_ID` is a per-stage env var set in `stacks/EnvVariablesStack.ts`
 * to a canonical lowercase UUID. Pinned as the UUID shape rather than the literal,
 * so the schema carries no stage-specific value and works for dev and prod alike.
 */
export const PARAGON_PROJECT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * One element of an org's `virtuousProjects` list, as `virtuous_webhook.py:update_project`
 * constructs it: exactly three keys, closed BY CODE (the only later mutation site is the
 * same function's `for project in projects` loop, which reassigns `name`/`isActive` and
 * adds nothing; `delete_project` only removes list elements). The VALUES are uncoerced
 * caller input — `"isActive": 1` is truthy, takes the update branch, and is stored
 * verbatim. This is an EFFECT shape (a DynamoDB write the port must replay), not part
 * of any HTTP response in the legacy repo.
 */
export const virtuousProjectEntrySchema = z
  .object({
    // `virtuous_webhook.py:181` — `project.get('id', None)`, uncoerced.
    id: uncoercedCallerValueSchema,
    // `virtuous_webhook.py:182` — `project.get('name', None)`, uncoerced.
    name: uncoercedCallerValueSchema,
    // `virtuous_webhook.py:183` — `project.get('isActive', None)`, uncoerced,
    // only ever truthiness-tested.
    isActive: uncoercedCallerValueSchema,
  })
  .strict();
export type VirtuousProjectEntry = z.infer<typeof virtuousProjectEntrySchema>;
